/**
 * QuotaThresholdNudge — dismissible modal that fires the FIRST time any
 * V4 quota crosses 80% usage. Aim is aha-moment upgrade psychology: users
 * see the value they're getting AS they hit the ceiling, not after.
 *
 * Rules:
 *   • Fires for Free and Plus users only. Pro (top tier) never nudged.
 *   • Only fires when ≥80% AND <100% used (crossing threshold, not exhausted).
 *   • Dismissal persists for 7 days per quota-kind in localStorage — so
 *     someone who hits 80% Ask isn't nagged again about Ask this week,
 *     but WILL be shown a different nudge if they cross 80% video-min later.
 *   • One nudge per session — even if multiple quotas are near-limit, we
 *     surface only the most-urgent one (highest utilization).
 *
 * Reads from `/api/subscriptions/status` fields exposed by V4 backend:
 *   premium_used/limit · ask_used/limit · video_minutes_used/limit · storage_used_mb/max_mb
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Sparkles, MessageCircle, Clock, HardDrive, X, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const DISMISS_KEY = (kind) => `brainsync.quota_nudge.dismiss.${kind}`;
const DISMISS_DAYS = 7;

// Threshold at which we start nudging. 80% = user has seen the aha
// moment but has runway to convert — perfect upgrade window.
const THRESHOLD = 0.8;

/**
 * Return the most-urgent quota that crossed the nudge threshold and
 * whose dismissal cookie has expired. Returns null when nothing to nudge.
 */
function pickTargetQuota(sub) {
    if (!sub) return null;
    const tier = sub.tier || 'free';
    if (tier === 'pro_unlimited') return null; // never nudge top tier

    const quotas = [
        {
            kind: 'premium',
            used: sub.premium_used ?? 0,
            limit: sub.premium_limit ?? 0,
            title: 'AI-powered saves',
            icon: Sparkles,
            labelSingular: 'Premium AI save',
            labelPlural: 'Premium AI saves',
            hint: tier === 'free'
                ? 'These are your lifetime AI saves. Upgrade unlocks 50/mo (Plus) or 200/mo (Pro).'
                : 'Renews next billing period, but upgrading to Pro gives you 200/mo instead of 50.',
        },
        {
            kind: 'ask',
            used: sub.ask_used ?? 0,
            limit: sub.ask_limit ?? 0,
            title: 'Ask BrainSync questions',
            icon: MessageCircle,
            labelSingular: 'Ask question',
            labelPlural: 'Ask questions',
            hint: 'Ask is the magic — chat with your library. Plus gives you 50/mo, Pro 150/mo.',
        },
        {
            kind: 'video-min',
            used: sub.video_minutes_used ?? 0,
            limit: sub.video_minutes_limit ?? 0,
            title: 'Video transcription minutes',
            icon: Clock,
            labelSingular: 'video minute',
            labelPlural: 'video minutes',
            hint: 'Long podcasts + video eat this fast. Plus 250 min/mo, Pro 600 min/mo.',
        },
        {
            kind: 'storage',
            used: sub.storage_used_mb ?? 0,
            limit: sub.storage_max_mb ?? 0,
            title: 'Attachment storage',
            icon: HardDrive,
            labelSingular: 'MB',
            labelPlural: 'MB',
            hint: 'Voice notes + PDF uploads fill this over time. Plus 2 GB, Pro 10 GB.',
        },
    ];

    // Only nudge on quotas in the "near limit" band (80-99%).
    const candidates = quotas
        .filter((q) => q.limit > 0)
        .map((q) => ({ ...q, ratio: q.used / q.limit }))
        .filter((q) => q.ratio >= THRESHOLD && q.ratio < 1.0)
        // Filter out dismissed ones (within DISMISS_DAYS)
        .filter((q) => {
            try {
                const ts = Number(localStorage.getItem(DISMISS_KEY(q.kind)) || 0);
                if (!ts) return true;
                const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
                return ageDays >= DISMISS_DAYS;
            } catch {
                return true;
            }
        })
        .sort((a, b) => b.ratio - a.ratio);

    return candidates[0] || null;
}

export default function QuotaThresholdNudge({ subscriptionInfo, onUpgrade }) {
    const target = useMemo(() => pickTargetQuota(subscriptionInfo), [subscriptionInfo]);
    const [dismissed, setDismissed] = useState(false);

    // Reset dismissed state whenever the target quota changes (e.g. user
    // hits a different cap later in the session)
    useEffect(() => {
        setDismissed(false);
    }, [target?.kind]);

    if (!target || dismissed) return null;

    const handleDismiss = () => {
        try {
            localStorage.setItem(DISMISS_KEY(target.kind), String(Date.now()));
        } catch { /* ignore */ }
        setDismissed(true);
    };

    const handleUpgrade = () => {
        handleDismiss(); // treat as dismissed so we don't re-fire
        if (onUpgrade) onUpgrade({ reason: target.kind, quota: target });
    };

    const percent = Math.round(target.ratio * 100);
    const remaining = Math.max(0, target.limit - target.used);
    const Icon = target.icon;

    return (
        <div
            className="fixed inset-x-0 bottom-4 sm:bottom-6 z-50 flex justify-center px-4 animate-in fade-in slide-in-from-bottom-4 duration-500"
            data-testid="quota-threshold-nudge"
            role="dialog"
            aria-live="polite"
        >
            <div className="max-w-md w-full bg-card border border-amber-200 shadow-2xl rounded-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-amber-50 to-emerald-50 px-5 py-3 flex items-center justify-between border-b border-amber-100">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-amber-700" />
                        </div>
                        <div>
                            <p className="text-xs font-semibold text-amber-900 tracking-wide uppercase">
                                {percent}% used
                            </p>
                            <p className="text-sm font-semibold text-foreground">
                                Only {remaining} {remaining === 1 ? target.labelSingular : target.labelPlural} left
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleDismiss}
                        className="text-muted-foreground hover:text-foreground p-1 rounded-md transition-colors"
                        aria-label="Dismiss"
                        data-testid="quota-nudge-dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="px-5 py-4 space-y-3">
                    <p className="text-sm text-foreground/90 leading-relaxed">
                        You&apos;re close to your {target.title.toLowerCase()} limit.
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                        {target.hint}
                    </p>
                    <div className="flex items-center gap-2 pt-1">
                        <Button
                            onClick={handleUpgrade}
                            className="flex-1"
                            data-testid="quota-nudge-upgrade"
                        >
                            See upgrade options
                            <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleDismiss}
                            data-testid="quota-nudge-later"
                        >
                            Later
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
